import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useImperativeHandle, useState } from "react";
import CancelIcon from "../../../assets/icons/cross-bold-icon.png";
import GlobalButton from "../../../components/button";
import Label from "../../../components/label";
import AppLoader from "../../../components/loader/index";
import CustomDropDown from "../../../components/select";
import MultiSelect from "../../../components/multiSelect";
import TextInput from "../../../components/textInput";
import {
  createDiscount,
  updateDiscount,
} from "../../../services/DiscountApiFn";
import { handleApiCall } from "../../../utils/helper";
import { addEditDiscountValidation } from "../../../utils/Validation";
import CustomCheckbox from "../../../components/checkbox";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiPaper-root": {
    width: "600px",
    [theme.breakpoints.down("sm")]: { margin: 12 },
  },
}));

const AddEditDiscountDialog = forwardRef(({ }, ref) => {
  const { palette } = useTheme();
  const [open, setOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const [inputValues, setInputValues] = useState({
    name: "",
    month: "",
    percentage: "",
    minimumPrice: "",
    length: [],
    isHalfPrice: false,
  });

  const [inputError, setInputError] = useState({
    name: false,
    month: false,
    percentage: false,
    minimumPrice: false,
    length: false,
    isHalfPrice: false,
  });

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      setDialogData(params);

      if (params.type === "edit") {
        const { data } = params;
        setInputValues({
          name: data?.code,
          percentage: data?.percentage,
          month: data?.validMonths,
          length: Array.isArray(data?.contractLengths)
            ? data?.contractLengths
            : [data?.contractLengths].filter(Boolean),
          minimumPrice: data?.minimumPrice,
          isHalfPrice: data?.isHalfPrice,
        });
      }
    },
    disable() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues({});
  };

  const handleChangeInputValue = (event) => {
    const { name, value } = event.target;
    setInputError((prev) => ({ ...prev, [name]: false }));
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddEditEquipment = async () => {
    const validate = addEditDiscountValidation(inputValues, setInputError);
    if (validate) {
      return;
    }

    const payload = {
      code: inputValues.name,
      percentage: parseInt(inputValues.percentage),
      minimumPrice: parseInt(inputValues.minimumPrice),
      validMonths: parseInt(inputValues.month),
      contractLengths: inputValues.length,
      isHalfPrice: inputValues.isHalfPrice,
    };

    if (dialogData?.type === "add") {
      handleApiCall(
        "",
        createDiscount,
        payload,
        setIsLoading,
        handleClose,
        dialogData.getData,
      );
    } else {
      handleApiCall(
        dialogData?.data?._id,
        updateDiscount,
        payload,
        setIsLoading,
        handleClose,
        dialogData.getData,
      );
    }
  };

  const useStyle = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      "& .MuiOutlinedInput-input": {
        padding: "12px 16px",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1px solid ${palette.text.coolGrey}`,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `1px solid ${palette.text.coolGrey} !important`,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `1px solid ${palette.text.coolGrey} !important`,
      },
    },
    "& .Mui-error": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #d32f2f !important`,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #d32f2f !important`,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      marginTop: 0,
    },
    "& .Mui-disabled": {
      cursor: "not-allowed",
    },
  };

  return (
    <BootstrapDialog onClose={handleClose} open={open}>
      <AppLoader isVisible={isLoading} />
      <Stack
        p={2}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography
          variant="h1"
          fontSize={"1.5rem"}
          fontWeight={500}
          color={"secondary.dark"}
        >
          {dialogData?.type === "add" ? "Add Discount" : "Edit Discount"}
        </Typography>
        <IconButton onClick={handleClose}>
          <img src={CancelIcon} alt="cancel-icon" width={25} />
        </IconButton>
      </Stack>
      <DialogContent dividers>
        <Box margin={"auto"}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Label
                title={"Discount Name"}
                required
                htmlFor={"discount-name"}
              />
              <TextInput
                id={"discount-name"}
                type={"text"}
                required
                error={inputError.name}
                helperText={inputError.name && "Code name is required"}
                defaultStyle={true}
                placeholder={"Enter code name"}
                value={inputValues.name}
                name={"name"}
                onChange={handleChangeInputValue}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label
                title={"Set Percentage"}
                required
                htmlFor={"set-percentage"}
              />
              <TextInput
                id={"set-percentage"}
                type={"number"}
                required
                error={inputError.percentage}
                helperText={inputError.percentage && "Percentage is required"}
                defaultStyle={true}
                placeholder={"Enter percentage"}
                value={inputValues.percentage}
                name={"percentage"}
                onChange={handleChangeInputValue}
                InputEndIcon={"%"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label
                title={"Select No. of Month"}
                required
                htmlFor={"monthly-price"}
              />
              <CustomDropDown
                id={"monthly-price"}
                required
                value={inputValues.month}
                name={"month"}
                onChange={handleChangeInputValue}
                stylePropos={useStyle}
                inputError={inputError.month}
                helperText={inputError.month && "Select discount month"}
              >
                <MenuItem value={0}>
                  Free
                </MenuItem>

                {[...Array(36)].map((val, ind) => {
                  return (
                    <MenuItem key={val} value={ind + 1}>
                      {ind + 1}
                    </MenuItem>
                  );
                })}
              </CustomDropDown>
            </Grid>
            <Grid item xs={12} md={6}>
              <Label
                title={"Contract Length"}
                required
                htmlFor={"contract-length"}
              />
              <MultiSelect
                id={"contract-length"}
                required
                value={inputValues.length}
                name={"length"}
                onChange={handleChangeInputValue}
                stylePropos={useStyle}
                error={inputError.length}
                helperText={inputError.length && "Select contract length"}
                options={[
                  { value: 12, label: "12" },
                  { value: 18, label: "18" },
                  { value: 24, label: "24" },
                  { value: 36, label: "36" },
                ]}
                placeholder="Select contract lengths"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Label title={"Min Price"} required htmlFor={"min-price"} />
              <TextInput
                id={"min-price"}
                type={"number"}
                required
                error={inputError.minimumPrice}
                helperText={
                  inputError.minimumPrice && "Minimum price is required"
                }
                defaultStyle={true}
                placeholder={"Enter min price"}
                value={inputValues.minimumPrice}
                name={"minimumPrice"}
                onChange={handleChangeInputValue}
                InputEndIcon={"£"}
              />
            </Grid>
            <Grid item xs={12}>
            <Stack gap={1} direction={"row"} alignItems={"center"}>
                <CustomCheckbox
                  isChecked={inputValues.isHalfPrice}
                  handleChecked={() =>
                    setInputValues((pre) => ({
                      ...pre,
                      isHalfPrice: !pre.isHalfPrice,
                    }))
                  }
                />
                <Typography>Half Price</Typography>
              </Stack>
          </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <GlobalButton
          variant={"custom_gradient"}
          handleClickBtn={handleClose}
          title={"Cancel"}
          style={{
            width: 100,
            background: "red !important",
          }}
        />
        <GlobalButton
          variant={"custom_gradient"}
          title={dialogData?.type === "add" ? "Add" : "Update"}
          style={{ width: 100 }}
          handleClickBtn={handleAddEditEquipment}
        />
      </DialogActions>
    </BootstrapDialog>
  );
});

export default AddEditDiscountDialog;
